import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import moment from 'moment'
import { useSelector } from 'react-redux'

import { selectUser } from '../../../../reducers/UserReducer'
import Settings from './Settings'
import SchoolQuotasTable from './SchoolQuotasTable'
import SchoolQuotasHeader from './SchoolQuotasHeader'
import QuotaFormModalProvider from '../../../../Providers/Forms/QuotaForm/QuotaFormModalProvider'
import QuotaToolBar from './QuotaToolBar'
import { InstitutionsQuotasSettings } from '../../../../Components/antd/Tables/DragTable/OverlaySettings'
import { useLocalStorage } from '../../../../hooks/UseLocalStorage'
import LOCAL_STORAGE_KEYS, { localStorage } from '../../../../utils/localStorage'
import { getSectorQuotas, getSectorStudentPathways } from '../../../../utils/api/sector'
import { firstOrDefault, getIdOrNull, isNonEmptyArray, isObject } from '../../../../utils'
import { InstitutionContext } from '../../../../Providers/InstitutionProvider'
import { SerializationContexts } from '../../../../utils/api/quota'
import { getQuotasMap, updateQuotaByActions } from '../../../../Components/shared/Quotas/utils'
import { buildSchoolQuota, isQuotaInSchools } from './utils'
import { TimePeriod, PeriodTypes } from '../../../../utils/timePeriods'
import useRequest from '../../../../hooks/useRequest'
import { getSchoolPeriods } from '../../../../utils/api/schoolPeriods'
import { SchoolPeriod } from '../../../../utils/entities/schoolPeriod'
import { OrderBy, OrderDirections } from '../../../../utils/constants'
import { Quota, QuotaValidators } from '../../../../utils/entities/quota'
import { API_DATE } from '../../../../utils/momentjs'
import { getBySector } from '../../../../utils/api/sectionGroup'
import { findSection, SectionGroup } from '../../../../utils/entities/sectionGroup'
import useQuota from '../../../../utils/api/hooks/useQuota'

import '../../../../assets/quota-allocation.scss'

const KEYS_TO_VALIDATE = Object.keys(QuotaValidators)

const SchoolQuotasView = () => {
  const { coloredSchools } = useContext(InstitutionContext)
  const user = useSelector(selectUser)

  const [period, setPeriod] = useState(new TimePeriod(moment(), 26, PeriodTypes.WEEK))
  const [sector, setSector] = useState(null)
  const [rawQuotas, setRawQuotas] = useState([])
  const [studentPathways, setStudentPathways] = useState([])
  const [sectionGroups, setSectionGroups] = useState([])
  const [isYearly, setIsYearly] = useState(false)
  const [selectedQuotas, setSelectedQuotas] = useState([])
  const [schools, setSchools] = useLocalStorage(LOCAL_STORAGE_KEYS.QUOTAS_TABLE.SELECTED_SCHOOLS, [])
  const [overlaySettings, setOverlaySettings] = useLocalStorage(LOCAL_STORAGE_KEYS.QUOTAS_TABLE.OVERLAY_SETTINGS, new InstitutionsQuotasSettings({ displayPlacesRequests: true }), (value) => new InstitutionsQuotasSettings(value))
  const [isUpdating, setIsUpdating] = useState(false)

  const config = useMemo(() => ({ overlay: overlaySettings }), [overlaySettings])

  useEffect(() => {
    if (isObject(sector)) {
      const { startDate, endDate } = period

      setRawQuotas(quotas => quotas.filter(q => q.startDate >= startDate && q.endDate <= endDate))

      getSectorQuotas(user, sector, { startDate: startDate.format(API_DATE), endDate: endDate.format(API_DATE), contexts: [SerializationContexts.LABELS] }).then(({ data }) => {
        setRawQuotas(data)
      })

      getSectorStudentPathways(user, sector, { contexts: [SerializationContexts.SECTION] }).then(({ data }) => {
        setStudentPathways(data)
      })

      getBySector(user, sector).then(({ data }) => {
        setSectionGroups(data.map(group => new SectionGroup(group)))
      })
    }
  }, [sector, user.id, period])

  const rawQuotasMap = useMemo(() => getQuotasMap(rawQuotas, q => buildSchoolQuota(q, coloredSchools)), [rawQuotas])
  const quotasMap = useMemo(() => {
    const copyQuotasMap = { ...rawQuotasMap }

    if (overlaySettings.filterQuotasBySchools) {
      copyQuotasMap.quotas = copyQuotasMap.quotas.filter(isQuotaInSchools(schools))
    }

    if (overlaySettings.displayPlacesRequests) {
      if (overlaySettings.filterPlacesRequestsBySchools) {
        copyQuotasMap.desideratas = copyQuotasMap.desideratas.filter(isQuotaInSchools(schools))
      }

      copyQuotasMap.quotas = copyQuotasMap.quotas.concat(copyQuotasMap.desideratas)
    }

    return copyQuotasMap
  }, [rawQuotasMap, sectionGroups, schools, overlaySettings])

  const quotasToUpdate = useMemo(() => {
    const quotas = []

    if (isNonEmptyArray(sectionGroups) && !isUpdating) {
      quotasMap.quotas.filter(q => isObject(q.section)).forEach(q => {
        const sectionGroup = sectionGroups.find(g => findSection(g, q.section))

        if (isObject(sectionGroup) && !quotas.some(quota => quota.id === q.id)) {
          quotas.push({ ...q, sectionGroup, section: null })
        }
      })
    }

    return quotas
  }, [quotasMap, sectionGroups, isUpdating])

  const handleQuotaChange = useCallback((...params) => {
    const { data: newQuotas, isDeleteAction } = updateQuotaByActions([...rawQuotas], params, period, LOCAL_STORAGE_KEYS.QUOTAS_TABLE.QUOTAS_MATRIX)

    setRawQuotas(newQuotas)

    if (isDeleteAction) {
      setSelectedQuotas([])
    }
  }, [rawQuotas, period])

  const quotaHandler = useQuota({ callback: handleQuotaChange })

  useEffect(() => {
    if (!isUpdating && isNonEmptyArray(quotasToUpdate)) {
      setIsUpdating(true)

      quotaHandler.update(quotasToUpdate.map(q => new Quota(q).toRequestBody(true))).then(() => {
        setIsUpdating(false)
      })
    }
  }, [isUpdating, quotasToUpdate, quotaHandler.update])

  const handleDateChange = useCallback(date => {
    setSelectedQuotas([])
    setPeriod(period => new TimePeriod(date, period.quantity, PeriodTypes.WEEK))
  }, [])

  const handlePeriodChange = useCallback(quantity => {
    setSelectedQuotas([])
    setPeriod(period => new TimePeriod(period.startDate, quantity, PeriodTypes.WEEK))
  }, [])

  const handleSelectSetting = useCallback((setting, isSelected) => {
    setOverlaySettings(current => current.clone({ [setting]: isSelected }))
  }, [setOverlaySettings])

  const holidays = useRequest({
    request: getSchoolPeriods,
    enabled: schools.length === 1,
    parameters: useMemo(() => ({
      filters: { school: getIdOrNull(schools[0]) },
      order: { by: OrderBy.START_DATE, direction: OrderDirections.ASC }
    }), [schools[0]]),
    select: useCallback(holidays => holidays.map(holiday => getIdOrNull(holiday) ? new SchoolPeriod(holiday) : null), [])
  })

  const handleResetGravity = useCallback(() => {
    quotasMap.quotas.forEach(q => {
      localStorage.removeValue(LOCAL_STORAGE_KEYS.QUOTAS_TABLE.QUOTAS_MATRIX, q.id)
    })
    quotasMap.desideratas.forEach(q => {
      localStorage.removeValue(LOCAL_STORAGE_KEYS.QUOTAS_TABLE.QUOTAS_MATRIX, q.id)
    })

    setRawQuotas(quotas => [...quotas])
  }, [quotasMap])

  return (
    <div className='school-quotas-container flex-row h-100 w-100'>
      <QuotaFormModalProvider
        sectionGroups={isObject(sector) ? { [getIdOrNull(sector)]: sectionGroups } : []}
        storageKey={LOCAL_STORAGE_KEYS.QUOTAS_TABLE.QUOTAS_MATRIX}
        studentPathways={studentPathways}
        keysToValidate={KEYS_TO_VALIDATE}
      >
        <div className='settings-container'>
          <Settings schools={schools} onSelectSector={setSector} onSelectSchool={setSchools} />
        </div>
        <div className='quotas-table-container h-100'>
          <div className='quotas-table-header w-100 pd-btm-8'>
            <SchoolQuotasHeader
              sector={sector}
              period={period}
              isYearly={isYearly}
              selectedQuota={selectedQuotas}
              overlaySettings={overlaySettings}
              onDateChange={handleDateChange}
              onPeriodChange={handlePeriodChange}
              onIsYearlyChange={setIsYearly}
              onQuotaChange={handleQuotaChange}
              onOverlaySettingsSelection={handleSelectSetting}
              onResetGravity={handleResetGravity}
            />
          </div>
          <div className='quotas-table-content w-100'>
            <SchoolQuotasTable
              isYearly={isYearly}
              school={firstOrDefault(schools)}
              sector={sector}
              studentPathways={studentPathways}
              sectionGroups={sectionGroups}
              period={period}
              holidayPeriods={holidays.data}
              quotasMap={quotasMap}
              selectedQuotas={selectedQuotas}
              tableConfig={config}
              onSelect={setSelectedQuotas}
              onChange={handleQuotaChange}
            />
          </div>
          {isNonEmptyArray(selectedQuotas) && (
            <QuotaToolBar
              selectedQuotas={selectedQuotas}
              onChange={handleQuotaChange}
              onUnselect={() => setSelectedQuotas([])}
            />
          )}
        </div>
      </QuotaFormModalProvider>
    </div>
  )
}

export default SchoolQuotasView
